/**
 * useReviewSummary — one shared read of GET /v3/risk/compliance-reviews/summary for the nav badge, the Home tile and
 * the queue's tab counts. A review calls refreshReviewSummary() so every count updates at once.
 */
import { useEffect, useState } from 'react';
import { loadReviewSummary, type ReviewSummary } from '@/services/complianceReviewsApi';

export interface SummaryState {
  readonly summary: ReviewSummary | null;
  readonly error: string | null;
  readonly loading: boolean;
}

let state: SummaryState = { summary: null, error: null, loading: false };
let inflight: Promise<void> | null = null;
let loadedOnce = false;
const listeners = new Set<(s: SummaryState) => void>();

function publish(next: SummaryState): void {
  state = next;
  listeners.forEach((l) => l(state));
}

/** Reads the summary again (after a review, or on first use). */
export function refreshReviewSummary(): Promise<void> {
  if (inflight) return inflight;
  publish({ ...state, loading: true });
  inflight = loadReviewSummary()
    .then((summary) => publish({ summary, error: null, loading: false }))
    .catch((e: unknown) => publish({ summary: state.summary, error: e instanceof Error ? e.message : String(e), loading: false }))
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useReviewSummary(): SummaryState {
  const [s, setS] = useState<SummaryState>(state);
  useEffect(() => {
    listeners.add(setS);
    if (!loadedOnce) {
      loadedOnce = true;
      void refreshReviewSummary();
    } else {
      setS(state);
    }
    return () => {
      listeners.delete(setS);
    };
  }, []);
  return s;
}

/** Tests only: forget the shared read. */
export function resetReviewSummaryForTests(): void {
  state = { summary: null, error: null, loading: false };
  inflight = null;
  loadedOnce = false;
  listeners.clear();
}
